/**
 * endConnectionsLogic.js
 * 
 * Модуль для работы с торцевыми подключениями и заглушками/переходами
 * распределительного коллектора.
 * 
 * Содержит логику автоматического выбора заглушек и переходов при выборе фитингов.
 */

import fittingsData from '../fittingsData';
import {
  extractDnFromFlangeKey,
  getPipeDiameterForDn,
  extractSizeFromNakGaikaKey,
  getPipeDiameterForNakGaika,
  extractSizeFromShaibaNarRezba,
  getPipeDiameterForShaibaNarRezba,
  findConcentricReducer,
  findEllipticalCap,
} from './collectorUtils';

/**
 * Определяет нужны ли заглушка/переход для фитинга подключения
 * и возвращает обновления параметров
 * 
 * @param {string} fittingId - ID выбранного фитинга
 * @param {number} mainPipeDiameter - диаметр основной трубы (гребёнки)
 * @param {string} side - сторона ('left' или 'right')
 * @param {string} collector - коллектор ('supply' или 'return')
 * @returns {object} - объект с обновлениями параметров
 */
export const getCapUpdatesForConnectionFitting = (fittingId, mainPipeDiameter, side, collector, shape = 'round') => {
  if (!fittingId) return {};
  
  const updates = {};
  // Формируем два префикса:
  // 1. capPrefixPascal для has...Cap (например hasSupplyLeftCap)
  // 2. capPrefixCamel для остальных ключей (например supplyLeftCapElementType)
  const collectorCapitalized = collector.charAt(0).toUpperCase() + collector.slice(1);
  const sideCapitalized = side.charAt(0).toUpperCase() + side.slice(1);
  const capPrefixPascal = `${collectorCapitalized}${sideCapitalized}`; // SupplyLeft
  const capPrefixCamel = `${collector}${sideCapitalized}`; // supplyLeft
  
  // Для квадратных профилей всегда прямые заглушки (без переходов)
  const isSquare = shape === 'square';
  
  // Определяем тип фитинга
  const isFlange = fittingId?.startsWith('Flanec_');
  const isNippel = fittingId?.startsWith('Nippel_');
  const isMufta = fittingId?.startsWith('Mufta_');
  const isNakGaika = fittingId?.startsWith('Nak_Gaika_');
  const isShaibaNarRezba = fittingId?.startsWith('Shaib_Nar_Rezba_');
  
  // Для ниппелей: проверяем нужен ли переход
  if (isNippel) {
    const nippelDiameter = fittingsData[fittingId]?.diameter;
    
    // Для квадратных профилей переходы не используются - всегда прямая заглушка
    if (isSquare) {
      updates[`has${capPrefixPascal}Cap`] = true;
      updates[`${capPrefixCamel}CapElementType`] = 'cap';
      updates[`${capPrefixCamel}CapType`] = 'straight';
      return updates;
    }
    
    // Специальные случаи с переходами (только для круглых труб)
    // Труба 48.3, ниппель 1 1/4 (48.3) → переход 48.3х42.4
    if (mainPipeDiameter === 48.3 && fittingId === 'Nippel_1_1.4') {
      const reducer = findConcentricReducer(48.3, 42.4);
      if (reducer) {
        updates[`has${capPrefixPascal}Cap`] = true;
        updates[`${capPrefixCamel}CapElementType`] = 'reducer';
        updates[`${capPrefixCamel}Reducer`] = reducer.key;
        return updates;
      }
    }
    
    // Труба 60.3, ниппель 1 1/2 (48.3) → переход 60.3х48.3
    if (mainPipeDiameter === 60.3 && fittingId === 'Nippel_1_1.2') {
      const reducer = findConcentricReducer(60.3, 48.3);
      if (reducer) {
        updates[`has${capPrefixPascal}Cap`] = true;
        updates[`${capPrefixCamel}CapElementType`] = 'reducer';
        updates[`${capPrefixCamel}Reducer`] = reducer.key;
        return updates;
      }
    }
    
    // Труба 76.1, ниппель 2 (60.3) → переход 76.1х60.3
    if (mainPipeDiameter === 76.1 && fittingId === 'Nippel_2') {
      const reducer = findConcentricReducer(76.1, 60.3);
      if (reducer) {
        updates[`has${capPrefixPascal}Cap`] = true;
        updates[`${capPrefixCamel}CapElementType`] = 'reducer';
        updates[`${capPrefixCamel}Reducer`] = reducer.key;
        return updates;
      }
    }
    
    // Труба 88.9, ниппель 2 1/2 (76.1) → переход 88.9х76.1
    if (mainPipeDiameter === 88.9 && fittingId === 'Nippel_2_1/2') {
      const reducer = findConcentricReducer(88.9, 76.1);
      if (reducer) {
        updates[`has${capPrefixPascal}Cap`] = true;
        updates[`${capPrefixCamel}CapElementType`] = 'reducer';
        updates[`${capPrefixCamel}Reducer`] = reducer.key;
        return updates;
      }
    }
    
    // Труба 114.3, ниппель 3 (88.9): переход 114.3х88.9 исключён, дальше — эллиптическая заглушка
    if (mainPipeDiameter === 114.3 && fittingId === 'Nippel_3') {
      const reducer = findConcentricReducer(114.3, 88.9);
      if (reducer) {
        updates[`has${capPrefixPascal}Cap`] = true;
        updates[`${capPrefixCamel}CapElementType`] = 'reducer';
        updates[`${capPrefixCamel}Reducer`] = reducer.key;
        return updates;
      }
    }
    
    // Общий случай: проверяем совпадение диаметров
    if (nippelDiameter && nippelDiameter !== mainPipeDiameter) {
      // Диаметры НЕ совпадают - нужна эллиптическая заглушка
      const ellipticalCap = findEllipticalCap(mainPipeDiameter);
      if (ellipticalCap) {
        updates[`has${capPrefixPascal}Cap`] = true;
        updates[`${capPrefixCamel}CapElementType`] = 'cap';
        updates[`${capPrefixCamel}CapType`] = 'elliptical';
      }
    } else {
      // Диаметры совпадают - впритык (без заглушек)
      updates[`has${capPrefixPascal}Cap`] = false;
    }
  }
  
  // Для муфт: включаем заглушку (эллиптическую для круглых, прямую для квадратных)
  if (isMufta) {
    if (isSquare) {
      // Для квадратных профилей - только прямая заглушка, без концентрических переходов
      updates[`has${capPrefixPascal}Cap`] = true;
      updates[`${capPrefixCamel}CapElementType`] = 'cap';
      updates[`${capPrefixCamel}CapType`] = 'straight';
      return updates;
    } else {
      // Для круглых труб - эллиптическая заглушка
      const ellipticalCap = findEllipticalCap(mainPipeDiameter);
      if (ellipticalCap) {
        updates[`has${capPrefixPascal}Cap`] = true;
        updates[`${capPrefixCamel}CapElementType`] = 'cap';
        updates[`${capPrefixCamel}CapType`] = 'elliptical';
      }
    }
  }
  
  // Для фланцев (свободных и приварных): проверяем нужен ли переход или заглушка
  if (isFlange) {
    if (isSquare) {
      // Для квадратных профилей фланцы не используются с переходами - всегда прямая заглушка
      updates[`has${capPrefixPascal}Cap`] = true;
      updates[`${capPrefixCamel}CapElementType`] = 'cap';
      updates[`${capPrefixCamel}CapType`] = 'straight';
    } else {
      // Для круглых труб
      const dn = extractDnFromFlangeKey(fittingId);
      const flangePipeDiameter = getPipeDiameterForDn(dn);
      
      if (flangePipeDiameter && flangePipeDiameter === mainPipeDiameter) {
        // Диаметры совпадают — закрываем "Заглушки и переходы"
        updates[`has${capPrefixPascal}Cap`] = false;
      } else if (flangePipeDiameter && flangePipeDiameter !== mainPipeDiameter && mainPipeDiameter > flangePipeDiameter) {
        // Гребёнка больше диаметра фланца — нужен переход или заглушка
        const reducer = findConcentricReducer(mainPipeDiameter, flangePipeDiameter);
        if (reducer) {
          // Есть подходящий переход — включаем переход
          updates[`has${capPrefixPascal}Cap`] = true;
          updates[`${capPrefixCamel}CapElementType`] = 'reducer';
          updates[`${capPrefixCamel}Reducer`] = reducer.key;
        } else {
          // Нет перехода — включаем эллиптическую заглушку
          const ellipticalCap = findEllipticalCap(mainPipeDiameter);
          if (ellipticalCap) {
            updates[`has${capPrefixPascal}Cap`] = true;
            updates[`${capPrefixCamel}CapElementType`] = 'cap';
            updates[`${capPrefixCamel}CapType`] = 'elliptical';
          }
        }
      }
    }
  }
  
  // Для накидных гаек и шайб с наружной резьбой: заглушки (эллиптические для круглых, прямые для квадратных)
  if (isNakGaika || isShaibaNarRezba) {
    if (isSquare) {
      // Для квадратных профилей - прямая заглушка
      updates[`has${capPrefixPascal}Cap`] = true;
      updates[`${capPrefixCamel}CapElementType`] = 'cap';
      updates[`${capPrefixCamel}CapType`] = 'straight';
    } else {
      // Для круглых труб
      let pipeDiameter;
      if (isNakGaika) {
        const size = extractSizeFromNakGaikaKey(fittingId);
        pipeDiameter = getPipeDiameterForNakGaika(size);
      } else {
        const size = extractSizeFromShaibaNarRezba(fittingId);
        pipeDiameter = getPipeDiameterForShaibaNarRezba(size);
      }
      
      if (pipeDiameter && pipeDiameter !== mainPipeDiameter) {
        // Как у фланцев: гребёнка больше диаметра подводящей трубки — сначала ищем концентрический переход
        if (mainPipeDiameter > pipeDiameter) {
          const reducer = findConcentricReducer(mainPipeDiameter, pipeDiameter);
          if (reducer) {
            updates[`has${capPrefixPascal}Cap`] = true;
            updates[`${capPrefixCamel}CapElementType`] = 'reducer';
            updates[`${capPrefixCamel}Reducer`] = reducer.key;
          } else {
            const ellipticalCap = findEllipticalCap(mainPipeDiameter);
            if (ellipticalCap) {
              updates[`has${capPrefixPascal}Cap`] = true;
              updates[`${capPrefixCamel}CapElementType`] = 'cap';
              updates[`${capPrefixCamel}CapType`] = 'elliptical';
            }
          }
        } else {
          const ellipticalCap = findEllipticalCap(mainPipeDiameter);
          if (ellipticalCap) {
            updates[`has${capPrefixPascal}Cap`] = true;
            updates[`${capPrefixCamel}CapElementType`] = 'cap';
            updates[`${capPrefixCamel}CapType`] = 'elliptical';
          }
        }
      } else {
        // Диаметры совпадают → закрываем заглушки/переходы
        updates[`has${capPrefixPascal}Cap`] = false;
      }
    }
  }
  
  // Специальная логика для муфт на круглой гребёнке (согласовано с getCapRequirementForFitting)
  if (isMufta && !isSquare) {
    if (mainPipeDiameter === 42.4 && (fittingId === 'Mufta_1' || fittingId === 'Mufta_1_1.4')) {
      updates[`has${capPrefixPascal}Cap`] = false;
    } else if (
      mainPipeDiameter === 48.3 &&
      (fittingId === 'Mufta_1_1.4' || fittingId === 'Mufta_1_1.2')
    ) {
      updates[`has${capPrefixPascal}Cap`] = false;
    } else if (mainPipeDiameter === 60.3 && fittingId === 'Mufta_2') {
      updates[`has${capPrefixPascal}Cap`] = false;
    }
    // Специальные случаи с переходами
    // Труба 48.3, муфта 1 (42.4) → переход 48.3х42.4
    else if (mainPipeDiameter === 48.3 && fittingId === 'Mufta_1') {
      const reducer = findConcentricReducer(48.3, 42.4);
      if (reducer) {
        updates[`has${capPrefixPascal}Cap`] = true;
        updates[`${capPrefixCamel}CapElementType`] = 'reducer';
        updates[`${capPrefixCamel}Reducer`] = reducer.key;
      }
    }
    // Труба 60.3, муфта 1 1/4 (48.3) → переход 60.3х48.3
    else if (mainPipeDiameter === 60.3 && fittingId === 'Mufta_1_1.4') {
      const reducer = findConcentricReducer(60.3, 48.3);
      if (reducer) {
        updates[`has${capPrefixPascal}Cap`] = true;
        updates[`${capPrefixCamel}CapElementType`] = 'reducer';
        updates[`${capPrefixCamel}Reducer`] = reducer.key;
      }
    }
    // При диаметре гребенки 60.3 и муфте 1 1/2" - переход 60.3х48.3
    else if (mainPipeDiameter === 60.3 && fittingId === 'Mufta_1_1.2') {
      const reducer = findConcentricReducer(60.3, 48.3);
      if (reducer) {
        updates[`has${capPrefixPascal}Cap`] = true;
        updates[`${capPrefixCamel}CapElementType`] = 'reducer';
        updates[`${capPrefixCamel}Reducer`] = reducer.key;
      }
    }
    // Труба 76.1, муфта 1 1/2 (48.3) → переход 76.1х60.3
    else if (mainPipeDiameter === 76.1 && fittingId === 'Mufta_1_1.2') {
      const reducer = findConcentricReducer(76.1, 60.3);
      if (reducer) {
        updates[`has${capPrefixPascal}Cap`] = true;
        updates[`${capPrefixCamel}CapElementType`] = 'reducer';
        updates[`${capPrefixCamel}Reducer`] = reducer.key;
      }
    }
    // Труба 76.1, муфта 2 (60.3) → переход 76.1х60.3
    else if (mainPipeDiameter === 76.1 && fittingId === 'Mufta_2') {
      const reducer = findConcentricReducer(76.1, 60.3);
      if (reducer) {
        updates[`has${capPrefixPascal}Cap`] = true;
        updates[`${capPrefixCamel}CapElementType`] = 'reducer';
        updates[`${capPrefixCamel}Reducer`] = reducer.key;
      }
    }
  }
  
  return updates;
};

/**
 * Проверяет, нужно ли показывать поле "Вылет" для фитинга подключения
 * 
 * @param {string} fittingId - ID фитинга
 * @param {number} mainPipeDiameter - диаметр основной трубы
 * @returns {boolean} - true если нужно показывать поле "Вылет"
 */
export const shouldShowConnectionLengthField = (fittingId, mainPipeDiameter) => {
  if (!fittingId) return false;
  
  const isFlange = fittingId?.startsWith('Flanec_');
  const isNakGaika = fittingId?.startsWith('Nak_Gaika_');
  const isShaibaNarRezba = fittingId?.startsWith('Shaib_Nar_Rezba_');
  
  // Для накидных гаек: показываем поле "Вылет" ТОЛЬКО если диаметры НЕ совпадают
  if (isNakGaika) {
    const size = extractSizeFromNakGaikaKey(fittingId);
    const pipeDiameterForFitting = getPipeDiameterForNakGaika(size);
    const needsCapOrReducer = pipeDiameterForFitting && pipeDiameterForFitting !== mainPipeDiameter;
    return needsCapOrReducer;
  }
  
  // Для шайб с наружной резьбой: показываем поле "Вылет" ТОЛЬКО если диаметры НЕ совпадают
  if (isShaibaNarRezba) {
    const size = extractSizeFromShaibaNarRezba(fittingId);
    const pipeDiameterForFitting = getPipeDiameterForShaibaNarRezba(size);
    const needsCapOrReducer = pipeDiameterForFitting && pipeDiameterForFitting !== mainPipeDiameter;
    return needsCapOrReducer;
  }
  
  // Для фланцев показываем поле если диаметры не совпадают И гребёнка больше фланца
  if (isFlange) {
    const dn = extractDnFromFlangeKey(fittingId);
    const flangePipeDiameter = getPipeDiameterForDn(dn);
    const needsReducerOrCap = flangePipeDiameter && 
                              flangePipeDiameter !== mainPipeDiameter && 
                              mainPipeDiameter > flangePipeDiameter;
    return needsReducerOrCap;
  }
  
  // Обычный фитинг — показываем поле длины фитинга
  return true;
};

/**
 * Определяет какой параметр длины использовать: connectionLength или fittingLength
 * @param {string} fittingId - ID фитинга
 * @param {boolean} hasCap - есть ли заглушка/переход
 * @param {number} mainPipeDiameter - диаметр основной трубы
 * @returns {boolean} - true если использовать connectionLength, false если fittingLength
 */
export const shouldUseConnectionLength = (fittingId, hasCap, mainPipeDiameter) => {
  if (!fittingId) return false;
  
  const isFlange = fittingId.startsWith('Flanec_');
  const isNakGaika = fittingId.startsWith('Nak_Gaika_');
  const isShaibaNarRezba = fittingId.startsWith('Shaib_Nar_Rezba_');
  
  // Для накидных гаек и шайб с наружной резьбой всегда используем connectionLength
  if (isNakGaika || isShaibaNarRezba) {
    return true;
  }
  
  // Для фланцев - только если есть переход/заглушка или диаметры не совпадают
  if (isFlange) {
    const dn = extractDnFromFlangeKey(fittingId);
    const flangePipeDiameter = getPipeDiameterForDn(dn);
    const needsReducerOrCap = flangePipeDiameter && 
                              flangePipeDiameter !== mainPipeDiameter && 
                              mainPipeDiameter > flangePipeDiameter;
    return needsReducerOrCap || hasCap;
  }
  
  return false;
};

